import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    inject,
    OnInit,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';
import {
    TelXLButtonComponent,
    TelXLDropdownComponent,
    TelXLPanelComponent,
} from '@dxp/elements';
import { CustomerApiService } from '@dxp/core/api';
import {
    CustomFieldDefinitionType,
    ICustomerDetails,
    ICustomFieldDefinition,
    IDropdown,
} from '@dxp/core/models';
import { LoggerService } from '@dxp/core/services';
import { combineLatest, map, tap } from 'rxjs';
import { ValidateContactFieldDirective } from '@dxp/core/directives';
import { SimpleEmailValidator, UKNumberValidator } from '@dxp/core/validators';

@Component({
    selector: 'telxl-shared-end-customer-details-form',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        TelXLDropdownComponent,
        TelXLButtonComponent,
        TelXLPanelComponent,
        ValidateContactFieldDirective,
    ],
    templateUrl: './customer-details-form.component.html',
    styleUrl: './customer-details-form.component.scss',
})
export class TelXlEndCustomerDetailsFormComponent implements OnInit {
    readonly #destroyRef = inject(DestroyRef);
    readonly #fb = inject(FormBuilder);
    readonly #customerApiService = inject(CustomerApiService);
    readonly #logger = inject(LoggerService);

    cancelled = output();
    complete = output<ICustomerDetails>();

    readonly canSave = signal<boolean>(false);
    readonly customFieldDefinitions = signal<
        ICustomFieldDefinition[] | undefined
    >(undefined);

    customerForm!: FormGroup;
    #initialFormValue!: ICustomerDetails;

    ngOnInit() {
        this.#customerApiService
            .getCustomFields(null)
            .pipe(
                map(res => res.fieldDefinitions),
                tap(fieldDefinitions => {
                    this.#initForm(fieldDefinitions);
                    this.customFieldDefinitions.set(fieldDefinitions);
                }),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe();
    }

    #initForm(customFieldDefinitions: ICustomFieldDefinition[]) {
        this.customerForm = this.#fb.group(
            {
                firstName: [
                    {
                        value: '',
                        disabled: false,
                    },
                ],
                middleName: [
                    {
                        value: '',
                        disabled: false,
                    },
                ],
                lastName: [
                    {
                        value: '',
                        disabled: false,
                    },
                ],
                email: [
                    {
                        value: '',
                        disabled: false,
                    },
                    [SimpleEmailValidator()],
                ],
                phoneNumbers: this.#fb.array([]),
                customFields: this.#fb.array(
                    customFieldDefinitions.map(cfd => {
                        const fieldGroup = this.#fb.group({
                            fieldDefinitionId: [
                                {
                                    value: cfd.id,
                                    disabled: false,
                                },
                            ],
                            value: [
                                {
                                    value: '',
                                    disabled: false,
                                },
                            ],
                        } as Record<
                            string,
                            {
                                value: string | number;
                                disabled: boolean;
                            }[]
                        >);

                        return fieldGroup;
                    }),
                ),
            },
            { validators: this.#atLeastOneContactRequired() },
        );

        this.addPhoneNumber();

        combineLatest([
            this.customerForm.valueChanges.pipe(
                tap(() => this.#checkIfFormChanged()),
                takeUntilDestroyed(this.#destroyRef),
            ),
            this.customerForm.statusChanges,
        ]).subscribe(() => {
            this.canSave.set(
                this.customerForm.valid && this.#checkIfFormChanged(),
            );
        });

        this.#initialFormValue = this.customerForm.getRawValue();
    }

    get phoneNumbers(): FormArray {
        return this.customerForm.get('phoneNumbers') as FormArray;
    }

    get customFields(): FormArray {
        return this.customerForm.get('customFields') as FormArray;
    }

    #checkIfFormChanged(): boolean {
        const currentFormValue = this.customerForm.getRawValue();
        return (
            JSON.stringify(this.#initialFormValue) !==
            JSON.stringify(currentFormValue)
        );
    }

    #getCustomField(
        fieldDefinitionId: number,
    ): ICustomFieldDefinition | undefined {
        return (this.customFieldDefinitions() ?? []).find(
            def => def.id === fieldDefinitionId,
        );
    }

    getCustomFieldLabel(fieldDefinitionId: number): string {
        const definition = this.#getCustomField(fieldDefinitionId);
        return definition ? definition.label : 'Unknown Field';
    }

    getCustomFieldType(
        fieldDefinitionId: number,
    ): CustomFieldDefinitionType | 'Unknown' {
        const definition = this.#getCustomField(fieldDefinitionId);
        return definition ? definition.type : 'Unknown';
    }

    getCustomFieldOptions(fieldDefinitionId: number): IDropdown<string>[] {
        const definition = this.#getCustomField(fieldDefinitionId);
        const options = definition ? (definition.options ?? []) : [];

        return [...options.map(o => ({ label: o, value: o, disabled: false }))];
    }

    getSelectedOption(fieldDefinitionId: number): string {
        const customField = this.customFields.controls.find(
            cf => cf.get('fieldDefinitionId')?.value === fieldDefinitionId,
        );

        return customField ? customField.get('value')?.value : '';
    }

    onOptionSelected(fieldDefinitionId: number, selectedValue: string) {
        const customField = this.customFields.controls.find(
            cf => cf.get('fieldDefinitionId')?.value === fieldDefinitionId,
        );

        if (customField) {
            customField.get('value')?.setValue(selectedValue);
            customField.get('value')?.markAsDirty();
            customField.get('value')?.markAsTouched();
            this.customerForm.updateValueAndValidity();
        }
    }

    addPhoneNumber(
        id = 0,
        number = '',
        type: string | null = null,
        preferred = false,
    ) {
        this.phoneNumbers.push(
            this.#fb.group({
                id: [id],
                number: [
                    { value: number, disabled: false },
                    [UKNumberValidator()],
                ],
                type: [type],
                preferred: [preferred],
            }),
        );
    }

    removePhoneNumber(index: number) {
        this.phoneNumbers.removeAt(index);
    }

    onCancel() {
        this.cancelled.emit();
    }

    #atLeastOneContactRequired(): ValidatorFn {
        return (form: AbstractControl): ValidationErrors | null => {
            const email = form.get('email')?.value?.trim();
            const phoneNumbers =
                (form.get('phoneNumbers') as FormArray)?.controls || [];

            const hasPhoneNumber = phoneNumbers.some(phone =>
                phone.get('number')?.value?.trim(),
            );

            if (!email && !hasPhoneNumber) {
                return { atLeastOneContactRequired: true };
            }

            return null;
        };
    }

    create() {
        if (!this.customerForm.valid) return;

        const customer: ICustomerDetails = {
            customerId: '',
            externalProfiles: [],
            externalId: null,
            lastInteractionAt: null,
            createdAt: null,
            businessUnitId: null,
            title: null,
            name: null,
            locale: null,
            firstName: this.customerForm.get('firstName')?.value,
            middleName: this.customerForm.get('middleName')?.value,
            lastName: this.customerForm.get('lastName')?.value,
            email: this.customerForm.get('email')?.value,
            phoneNumbers: this.phoneNumbers.controls
                .filter(phone => phone.get('number')?.value.trim())
                .map(phone => ({
                    phoneNumberId: 0,
                    number: phone.get('number')?.value,
                    type: phone.get('type')?.value,
                    preferred: phone.get('preferred')?.value,
                })),
            customFields: this.customFields.controls
                .filter(customField => customField.get('value')?.value.trim())
                .map(customField => ({
                    fieldDefinitionId:
                        customField.get('fieldDefinitionId')?.value,
                    customFieldId: customField.get('customFieldId')?.value,
                    value: customField.get('value')?.value,
                })),
            preferredAgentId: null,
        };

        this.#customerApiService
            .addCustomer(customer)
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe({
                next: customer => {
                    this.complete.emit(customer);
                },
                error: customer =>
                    this.#logger.error('Failed to save customer', customer),
            });
    }
}
